import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { enquiryService, authService } from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import EnquiryForm from '../enquiry/EnquiryForm';
import ConversationModal from './ConversationModal';
import BDMetrics from './BDMetrics';
import FilePreviewModal from '../components/FilePreviewModal';
import AddItemModal from '../components/AddItemModal';
import { useSearchParams } from 'react-router-dom';
import TotalValue from '../components/TotalValue';
import AttachmentViewer from '../components/AttachmentViewer';
import {
  Box, Button, Typography, Paper, Grid, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Chip, Alert,
  Divider, Stack, TextField, Snackbar, InputAdornment, IconButton, Container, Pagination,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import '../styles/_utilities.css';
import { formatDate } from '../utils/DateUtils';

// #region Child Components
const EnquiryFilters = ({
  filters,
  onFilterChange,
  onClearFilters,
  bdmUsers,
  scmUsers,
  currentUser
}) => (
  <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 3, width: '100%', maxWidth: '100%' }}>
    <Typography variant="h6" gutterBottom>Filter Enquiries</Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Requested By</InputLabel>
          <Select
            name="requestedBy"
            value={filters.requestedBy}
            label="Requested By"
            onChange={onFilterChange}
          >
            <MenuItem value={currentUser}>My Enquiries</MenuItem>
            <MenuItem value=""><em>All BDMs</em></MenuItem>
            <Divider />
            {bdmUsers.filter(name => name !== currentUser).map(bdmUser => (
              <MenuItem key={bdmUser} value={bdmUser}>{bdmUser}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Filter by Status</InputLabel>
          <Select
            name="status"
            value={filters.status}
            label="Filter by Status"
            onChange={onFilterChange}
          >
            <MenuItem value=""><em>All Statuses</em></MenuItem>
            {["Pending with SCM", "Pending with BDM", "Closed Won", "Closed Lost", "On Hold", "Pending With Client"].map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Filter by SCM</InputLabel>
          <Select
            name="assignedTo"
            value={filters.assignedTo}
            label="Filter by SCM"
            onChange={onFilterChange}
          >
            <MenuItem value=""><em>All SCM Users</em></MenuItem>
            {scmUsers.map(scmUser => (
              <MenuItem key={scmUser} value={scmUser}>{scmUser}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={3} container alignItems="center" spacing={2}>
        <Grid item xs={6}>
          <TextField
            name="startDate"
            label="From"
            type="date"
            size="small"
            value={filters.startDate}
            onChange={onFilterChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            name="endDate"
            label="To"
            type="date"
            size="small"
            value={filters.endDate}
            onChange={onFilterChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid item xs={12} container justifyContent="flex-end">
        <Button variant="outlined" onClick={onClearFilters}>Clear Filters</Button>
      </Grid>
    </Grid>
  </Paper>
);

const EnquiryItemCard = ({
  item,
  enquiry,
  user,
  onItemInteraction,
  onOpenFilePreview,
  onSaveItemDetails,
  onUpdateItemStatus
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState(item);

  const handleFieldChange = (fieldName, value) => {
    setEditedItem(prev => ({ ...prev, [fieldName]: value }));
  };

  const onSave = () => {
    onSaveItemDetails(editedItem);
    setIsEditing(false);
  };

  const getStatusChipColor = (status) => {
    const s = status ? status.toLowerCase() : '';
    if (s.includes('closed won')) return 'success';
    if (s.includes('closed lost')) return 'error';
    if (s.includes('pending')) return 'warning';
    if (s.includes('on hold')) return 'info';
    return 'default';
  };

  return (
    <Paper sx={{ p: 2, boxShadow: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%' }}>
      <Box>
        <Typography variant="h4" component="h5" sx={{ fontWeight: 'bold', wordBreak: 'break-all' }}>{item.name}</Typography>
        {isEditing ? (
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12}><TextField label="Description" value={editedItem.description} onChange={e => handleFieldChange('description', e.target.value)} fullWidth size="small" /></Grid>
            <Grid item xs={6}><TextField label="Quantity" value={editedItem.quantity} onChange={e => handleFieldChange('quantity', e.target.value)} fullWidth size="small" type="number" /></Grid>
            <Grid item xs={6}><TextField label="Budget/Unit" value={editedItem.budgetPerUnit} onChange={e => handleFieldChange('budgetPerUnit', e.target.value)} fullWidth size="small" type="number" /></Grid>
            <Grid item xs={12}><TextField label="Delivery Location" value={editedItem.deliveryLocation} onChange={e => handleFieldChange('deliveryLocation', e.target.value)} fullWidth size="small" /></Grid>
            <Grid item xs={12}><TextField label="Target Date" value={editedItem.targetDateOfDelivery} onChange={e => handleFieldChange('targetDateOfDelivery', e.target.value)} fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>
        ) : (
          <Grid container spacing={0.5} sx={{ mt: 1 }}>
            <Grid item xs={12}><Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{item.description}</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2"><strong>Quantity:</strong> {item.quantity || 'N/A'}</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2"><strong>Budget/Unit:</strong> {item.budgetPerUnit ? item.budgetPerUnit.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }) : 'N/A'}</Typography></Grid>
            <Grid item xs={12}><Typography variant="body2"><strong>Location:</strong> {item.deliveryLocation || 'N/A'}</Typography></Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Target Date:</strong> {formatDate(item.targetDateOfDelivery)}
              </Typography>
            </Grid>
          </Grid>
        )}
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>Status:</strong> <Chip label={item.status} color={getStatusChipColor(item.status)} size="small" /></Typography>
        </Box>
        <AttachmentViewer attachments={item.imageAttachments} onOpenFilePreview={onOpenFilePreview} />
      </Box>
      <Box sx={{ mt: 2 }}>
        {(user.role === 'BUSINESS_DEVELOPMENT' || user.role === 'ADMIN') && (
          <Box sx={{ mb: 1 }}>
            {isEditing ? (
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={onSave} size="small">Save</Button>
                <Button variant="outlined" onClick={() => setIsEditing(false)} size="small">Cancel</Button>
              </Stack>
            ) : (
              <Button variant="outlined" onClick={() => setIsEditing(true)} size="small" fullWidth>Edit</Button>
            )}
            <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 1 }}>
              <InputLabel>Update Status</InputLabel>
              <Select value={item.status} onChange={(e) => onUpdateItemStatus(enquiry, item, e.target.value)} label="Update Status">
                {["Pending with SCM", "Pending with BDM", "Closed Won", "Closed Lost", "On Hold", "Pending With Client"].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        )}
        <Button onClick={() => onItemInteraction(enquiry, item)} variant="outlined" fullWidth size="small">View Conversation</Button>
      </Box>
    </Paper>
  );
};

const EnquiryCard = ({
  enquiry,
  user,
  scmUsers,
  onOverallStatusChange,
  onOverallAssignment,
  onOpenOverallConversation,
  onOpenAddItemModal,
  onItemInteraction,
  onOpenFilePreview,
  onSaveItemDetails,
  onUpdateItemStatus
}) => {
  const enquiryValue = enquiry.items ? enquiry.items.reduce((sum, item) => sum + (item.quantity || 0) * (item.budgetPerUnit || 0), 0) : 0;

  return (
    <Paper sx={{ p: 2, mb: 3, boxShadow: 1, width: '100%' }}>
      <Typography variant="h3" sx={{ mb: 1.5, wordBreak: 'break-all' }}>{enquiry.uniqueId}</Typography>
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6}><Typography variant="body1" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}><strong>Client:</strong> {enquiry.clientName}</Typography></Grid>
        <Grid item xs={12} sm={6}><Typography variant="body1" sx={{ wordBreak: 'break-all' }}><strong>Lead Source:</strong> {enquiry.leadSource || 'N/A'}</Typography></Grid>
        <Grid item xs={12} sm={6}><Typography variant="body1" sx={{ wordBreak: 'break-all' }}><strong>Requested By:</strong> {enquiry.requestedBy}</Typography></Grid>
        <Grid item xs={12} sm={6}><Typography variant="enquiryValue"><strong>Enquiry Value:</strong> {enquiryValue.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</Typography></Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" display="block">
            Created: {formatDate(enquiry.createdAt)}
          </Typography>
        </Grid>

        {/* Display Lost Reason if available */}
        {enquiry.status === 'Closed Lost' && enquiry.lostReason && (
            <Grid item xs={12} sm={6}>
                <Typography variant="body1" color="error">
                    <strong>Lost Reason:</strong> {enquiry.lostReason}
                </Typography>
            </Grid>
        )}

      </Grid>
      {user.role === 'BUSINESS_DEVELOPMENT' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Typography component="span" variant="body2" fontWeight="bold">Overall Status:</Typography>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={enquiry.status} onChange={(e) => onOverallStatusChange(enquiry.id, e.target.value)} label="Status">
              {["Pending with SCM", "Pending with BDM", "Closed Won", "Closed Lost", "On Hold", "Pending With Client"].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography component="span" variant="body2" fontWeight="bold" ml={{ sm: 2 }}>Assign Enquiry:</Typography>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Assign To</InputLabel>
            <Select value={enquiry.assignedTo || ''} onChange={(e) => onOverallAssignment(enquiry.id, e.target.value)} label="Assign To">
              <MenuItem value="">Unassigned</MenuItem>
              {scmUsers.map(scmUser => <MenuItem key={scmUser} value={scmUser}>{scmUser}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" color="secondary" onClick={() => onOpenOverallConversation(enquiry)} sx={{ ml: { sm: 2 }, flexShrink: 0 }}>View Overall Conversation</Button>
        </Stack>
      )}
      <AttachmentViewer attachments={enquiry.imageAttachments} onOpenFilePreview={onOpenFilePreview} />
      <Divider sx={{ my: 2 }} />
      <Typography variant="h5" component="h4" sx={{ mb: 2 }}>Items</Typography>
      <Grid container spacing={2}>
        {enquiry.items && enquiry.items.length > 0 ? (
          enquiry.items.map(item => (
            <Grid item xs={12} sm={6} md={4} key={item.id} sx={{ width: '100%' }}>
              <EnquiryItemCard
                item={item}
                enquiry={enquiry}
                user={user}
                onItemInteraction={onItemInteraction}
                onOpenFilePreview={onOpenFilePreview}
                onSaveItemDetails={onSaveItemDetails}
                onUpdateItemStatus={onUpdateItemStatus}
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}><Typography>No items for this enquiry.</Typography></Grid>
        )}
      </Grid>
      {user.role === 'BUSINESS_DEVELOPMENT' && (
        <Button variant="outlined" color="secondary" onClick={() => onOpenAddItemModal(enquiry)} sx={{ mt: 2, width: '100%' }}>
          + ADD NEW ITEM TO THIS ENQUIRY
        </Button>
      )}
    </Paper>
  );
};

const EnquiryList = ({ enquiries, ...props }) => (
  <Box component="ul" sx={{ listStyle: 'none', p: 0, width: '100%' }}>
    {enquiries.map(enquiry => (
      <EnquiryCard key={enquiry.id || enquiry.uniqueId} enquiry={enquiry} {...props} />
    ))}
  </Box>
);
// #endregion

function BDDashboard() {
  const { user } = useContext(AuthContext);
  const [enquiries, setEnquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateEnquiryForm, setShowCreateEnquiryForm] = useState(false);
  const [selectedEnquiryItemForModal, setSelectedEnquiryItemForModal] = useState(null);
  const [parentEnquiryForModal, setParentEnquiryForModal] = useState(null);
  const [scmUsers, setScmUsers] = useState([]);
  const [bdmUsers, setBdmUsers] = useState([]);
  const [usersError, setUsersError] = useState('');
  const [showFilePreviewModal, setShowFilePreviewModal] = useState(false);
  const [fileToPreview, setFileToPreview] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [selectedEnquiryToAddItem, setSelectedEnquiryToAddItem] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    assignedTo: '',
    startDate: '',
    endDate: '',
    requestedBy: user?.username || ''
  });

  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState('');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // States for Lost Reason Modal
  const [isLostReasonModalOpen, setIsLostReasonModalOpen] = useState(false);
  const [selectedLostReason, setSelectedLostReason] = useState('');
  const [customLostReason, setCustomLostReason] = useState('');
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null); // { enquiryId, status, isItemUpdate, itemObject }

  const lostReasonOptions = [
    "Competitor pricing issue",
    "Stock availability issue",
    "Alternate product",
    "Event cancelled",
    "Timeline",
    "Slow first response",
    "Weak follow-up",
    "Budget not qualified",
    "Client chose known vendor",
    "No mockups/visuals shared",
    "Poor proposal quality",
    "Internal delay (costing/sampling)",
    "No closure attempt",
    "Confusing communication / no SPOC",
    "Client went cold / no revival",
    "Other"
  ];

  const memoizedFilters = useMemo(() => filters, [filters]);

  const fetchUsers = useCallback(async () => {
    try {
      const [scmRes, bdmRes] = await Promise.all([
        authService.get('/auth/users', { params: { role: 'SUPPLY_CHAIN' } }),
        authService.get('/auth/users', { params: { role: 'BUSINESS_DEVELOPMENT' } })
      ]);
      setScmUsers(scmRes.data.map(u => u.username));
      setBdmUsers(bdmRes.data.map(u => u.username));
    } catch (err) {
      setUsersError('Failed to load users for filtering.');
    }
  }, []);

  const fetchFilteredEnquiries = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError('');
    try {
      const params = {
        requestedBy: filters.requestedBy || undefined,
        searchTerm: searchTerm.trim() || undefined,
        status: filters.status || undefined,
        assignedTo: filters.assignedTo || undefined,
        startDate: filters.startDate ? filters.startDate + 'T00:00:00' : undefined,
        endDate: filters.endDate ? filters.endDate + 'T23:59:59' : undefined,
        page: page - 1,
        size: 10
      };
      const response = await enquiryService.get('/enquiry', { params });
      setEnquiries(Array.isArray(response.data.content) ? response.data.content : []);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      setError('Failed to load enquiries. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user, searchTerm, filters, page]);

  useEffect(() => {
    const fetchMetrics = async () => {
      setMetricsLoading(true);
      setMetricsError('');
      try {
        const params = {
          requestedBy: memoizedFilters.requestedBy || undefined,
          assignedTo: memoizedFilters.assignedTo || undefined,
          status: memoizedFilters.status || undefined,
          startDate: memoizedFilters.startDate ? `${memoizedFilters.startDate}T00:00:00` : undefined,
          endDate: memoizedFilters.endDate ? `${memoizedFilters.endDate}T23:59:59` : undefined,
        };
        const response = await enquiryService.get('/enquiry/metrics', { params });
        setMetrics(response.data);
      } catch (err) {
        setMetricsError('Failed to load metrics. Please try again later.');
        console.error("Error fetching BD metrics:", err);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [memoizedFilters]);

  useEffect(() => {
    if (user) fetchUsers();
  }, [user, fetchUsers]);

  useEffect(() => {
    if (user && !filters.requestedBy) {
      setFilters(prev => ({ ...prev, requestedBy: user.username }));
    }
    const handler = setTimeout(() => {
      fetchFilteredEnquiries();
    }, 500);
    return () => clearTimeout(handler);
  }, [fetchFilteredEnquiries, user, filters.requestedBy]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '', assignedTo: '', startDate: '', endDate: '',
      requestedBy: user?.username || ''
    });
    setSearchTerm('');
  };

  const handleEnquiryCreated = () => {
    setShowCreateEnquiryForm(false);
    fetchFilteredEnquiries();
  };

  const handleItemInteraction = (enquiry, item) => {
    setParentEnquiryForModal(enquiry);
    setSelectedEnquiryItemForModal(item);
  };

  const handleOpenOverallEnquiryConversation = (enquiry) => {
    setSelectedEnquiryItemForModal(null);
    setParentEnquiryForModal(enquiry);
  };

  const handleCloseConversationModal = () => {
    setParentEnquiryForModal(null);
    setSelectedEnquiryItemForModal(null);
    fetchFilteredEnquiries();
  };

  const handleSaveItemDetails = async (item) => {
    try {
      const { id, name, description, quantity, budgetPerUnit, deliveryLocation, targetDateOfDelivery, status } = item;
      await enquiryService.put(`/enquiry/item/${id}`, {
        name,
        description,
        quantity: quantity ? parseInt(quantity, 10) : null,
        budgetPerUnit: budgetPerUnit ? parseFloat(budgetPerUnit) : null,
        deliveryLocation,
        targetDateOfDelivery,
        status,
      });
      setSnackbar({ open: true, message: `Item details saved successfully!`, severity: 'success' });
      fetchFilteredEnquiries();
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to save item details: ${err.response?.data?.message || 'Error'}`, severity: 'error' });
    }
  };

  const handleUpdateItemStatus = async (enquiry, item, newStatus) => {
    // Check if this update will close the entire enquiry as lost
    // Condition: This item is being set to Closed Lost AND all other items are already Closed Lost
    const isGlobalCloseLost = newStatus === 'Closed Lost' &&
      enquiry.items.every(i => i.id === item.id ? true : i.status === 'Closed Lost'); // For the current item we assume it becomes true

    if (isGlobalCloseLost) {
        setPendingStatusUpdate({
            enquiryId: enquiry.id,
            status: newStatus,
            isItemUpdate: true,
            itemObject: item
        });
        setSelectedLostReason('');
        setCustomLostReason('');
        setIsLostReasonModalOpen(true);
        return;
    }

    // Normal update without modal
    await executeItemStatusUpdate(item, newStatus);
  };

  const executeItemStatusUpdate = async (item, newStatus) => {
    try {
      const itemToUpdate = { ...item, status: newStatus };
      await enquiryService.put(`/enquiry/item/${item.id}`, itemToUpdate);
      setSnackbar({ open: true, message: `Item status updated to ${newStatus}.`, severity: 'success' });
      fetchFilteredEnquiries();
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to update item status: ${err.response?.data?.message || 'Error'}`, severity: 'error' });
    }
  };

  const handleOverallEnquiryStatusChange = async (enquiryId, newStatus) => {
    // If selecting Closed Lost, open modal instead of immediate update
    if (newStatus === "Closed Lost") {
        setPendingStatusUpdate({ enquiryId, status: newStatus, isItemUpdate: false });
        setSelectedLostReason(''); // Reset reason
        setCustomLostReason(''); // Reset custom reason
        setIsLostReasonModalOpen(true);
        return;
    }

    await executeStatusUpdate(enquiryId, newStatus);
  };

  const executeStatusUpdate = async (enquiryId, status, lostReason = null) => {
    try {
      const params = { status };
      if (lostReason) {
        params.lostReason = lostReason;
      }

      await enquiryService.put(`/enquiry/${enquiryId}/status`, null, { params });
      setSnackbar({ open: true, message: `Enquiry status updated to ${status}.`, severity: 'success' });
      fetchFilteredEnquiries();
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to update status: ${err.response?.data?.message || 'Error'}`, severity: 'error' });
    }
  };

  const handleLostReasonConfirm = async () => {
    const finalReason = selectedLostReason === 'Other' ? customLostReason : selectedLostReason;

    // Validation: Check if a reason is selected, and if "Other" is selected, check if text is entered
    if (!finalReason || !finalReason.trim()) {
        return;
    }

    if (!pendingStatusUpdate) {
        return;
    }

    try {
        if (pendingStatusUpdate.isItemUpdate) {
            // 1. Update the item
            // This triggers the backend to auto-set the Enquiry status to "Closed Lost" (with null reason)
            await enquiryService.put(`/enquiry/item/${pendingStatusUpdate.itemObject.id}`, {
                ...pendingStatusUpdate.itemObject,
                status: pendingStatusUpdate.status
            });

            // 2. Immediately patch the enquiry reason
            // We re-send 'Closed Lost' as status to maintain consistency and provide the reason
            const params = { status: 'Closed Lost', lostReason: finalReason };
            await enquiryService.put(`/enquiry/${pendingStatusUpdate.enquiryId}/status`, null, { params });

            setSnackbar({ open: true, message: `Item and Enquiry updated to Closed Lost.`, severity: 'success' });
        } else {
            // Direct overall status update from the dropdown
            await executeStatusUpdate(pendingStatusUpdate.enquiryId, pendingStatusUpdate.status, finalReason);
        }

        // Cleanup and Refresh
        fetchFilteredEnquiries();
        setIsLostReasonModalOpen(false);
        setPendingStatusUpdate(null);
        setSelectedLostReason('');
        setCustomLostReason('');

    } catch (err) {
        setSnackbar({ open: true, message: `Failed to update: ${err.response?.data?.message || 'Error'}`, severity: 'error' });
    }
  };

  const handleLostReasonCancel = () => {
    setIsLostReasonModalOpen(false);
    setPendingStatusUpdate(null);
    setSelectedLostReason('');
    setCustomLostReason('');
  };


  const handleOverallEnquiryAssignment = async (enquiryId, assignedToUsername) => {
    try {
      await enquiryService.put(`/enquiry/${enquiryId}/assign`, null, { params: { assignedTo: assignedToUsername } });
      setSnackbar({ open: true, message: `Enquiry assigned to ${assignedToUsername || 'Unassigned'}.`, severity: 'success' });
      fetchFilteredEnquiries();
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to assign enquiry: ${err.response?.data?.message || 'Error'}`, severity: 'error' });
    }
  };

    const handleOpenFilePreview = (attachment) => {
      if (!attachment || !attachment.id) return;
      enquiryService.get(`/enquiry/attachments/${attachment.id}/presigned-url`)
        .then(response => {
          setFileToPreview({ url: response.data, name: attachment.originalFileName, type: attachment.contentType });
          setShowFilePreviewModal(true);
        })
        .catch(err => setSnackbar({
          open: true,
          message: `Failed to get file preview: ${err.response?.data?.message || 'Error'}`,
          severity: 'error'
        }));
    };

    const handlePageChange = (event, value) => {
      setPage(value);
    };

  if (!user) return <Typography sx={{ p: 2 }}>Please log in to view your dashboard.</Typography>;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 1400,
          mx: 'auto',
          bgcolor: 'background.paper',
          p: 2,
          borderRadius: 2,
          boxShadow: 2,
          mb: 3,
          minHeight: '100vh',
          overflowX: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
          <Typography variant="h2" component="h2" sx={{ mr: 2, mb: { xs: 2, sm: 0 } }}>Business Development Dashboard</Typography>
          <Stack direction="row" spacing={2} sx={{ mb: { xs: 2, sm: 0 } }}>
            <TextField
              label="Search Enquiries" variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ endAdornment: searchTerm && <InputAdornment position="end"><IconButton onClick={() => setSearchTerm('')} size="small"><CloseIcon /></IconButton></InputAdornment> }}
              sx={{ minWidth: 200 }}
            />
            <Button variant="contained" color="success" onClick={() => setShowCreateEnquiryForm(p => !p)}>
              {showCreateEnquiryForm ? 'View My Enquiries' : '+ Create New Enquiry'}
            </Button>
          </Stack>
        </Box>

        {usersError && <Alert severity="error" sx={{ mb: 2 }}>{usersError}</Alert>}

        {showCreateEnquiryForm ? <EnquiryForm onEnquiryCreated={handleEnquiryCreated} /> : (
          <>
            <EnquiryFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              bdmUsers={bdmUsers}
              scmUsers={scmUsers}
              currentUser={user.username}
            />
            {/* START: Pass metrics state to child components */}
            <BDMetrics
              metrics={metrics}
              isLoading={metricsLoading}
              error={metricsError}
              onMetricClick={(f) => setFilters(prev => ({ ...prev, status: f.status || '' }))}
            />
            <TotalValue value={metrics?.totalValue} />
            <Typography variant="h3" component="h3" sx={{ mb: 2, mt: 4 }}>
              {filters.requestedBy === user.username ? 'My Enquiries' : 'Enquiries'}
            </Typography>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : enquiries.length === 0 ? (
              <Typography sx={{ textAlign: 'center', p: 3 }}>No enquiries found.</Typography>
            ) : (
              <EnquiryList
                enquiries={enquiries}
                user={user}
                scmUsers={scmUsers}
                onOverallStatusChange={handleOverallEnquiryStatusChange}
                onOverallAssignment={handleOverallEnquiryAssignment}
                onOpenOverallConversation={handleOpenOverallEnquiryConversation}
                onOpenAddItemModal={(enq) => setSelectedEnquiryToAddItem(enq)}
                onItemInteraction={handleItemInteraction}
                onOpenFilePreview={handleOpenFilePreview}
                onSaveItemDetails={handleSaveItemDetails}
                onUpdateItemStatus={handleUpdateItemStatus}
              />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          </>
        )}

        {(selectedEnquiryItemForModal || parentEnquiryForModal) && (
          <ConversationModal
            enquiry={parentEnquiryForModal}
            enquiryItem={selectedEnquiryItemForModal}
            onClose={handleCloseConversationModal}
            userRole={user.role}
            onOpenFilePreview={handleOpenFilePreview}
            isEnquiryLevel={!selectedEnquiryItemForModal}
          />
        )}
        {showFilePreviewModal && fileToPreview && (
          <FilePreviewModal
            fileUrl={fileToPreview.url}
            fileName={fileToPreview.name}
            fileContentType={fileToPreview.type}
            onClose={() => setShowFilePreviewModal(false)}
          />
        )}
        {selectedEnquiryToAddItem && (
          <AddItemModal
            enquiryId={selectedEnquiryToAddItem.id}
            enquiryUniqueId={selectedEnquiryToAddItem.uniqueId}
            onClose={() => setSelectedEnquiryToAddItem(null)}
            onItemsAdded={() => { setSelectedEnquiryToAddItem(null); fetchFilteredEnquiries(); }}
          />
        )}
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>

        {/* Lost Reason Modal */}
        <Dialog open={isLostReasonModalOpen} onClose={handleLostReasonCancel}>
            <DialogTitle>Reason for Lost Enquiry</DialogTitle>
            <DialogContent sx={{ pt: 1, minWidth: 300 }}>
                <Typography variant="body2" gutterBottom>
                    Please specify why this enquiry is being closed as lost.
                </Typography>
                <FormControl fullWidth margin="dense">
                    <InputLabel>Select Reason</InputLabel>
                    <Select
                        value={selectedLostReason}
                        label="Select Reason"
                        onChange={(e) => setSelectedLostReason(e.target.value)}
                    >
                        {lostReasonOptions.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                
                {/* Custom Reason Text Field - Shown only when 'Other' is selected */}
                {selectedLostReason === 'Other' && (
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Please specify the reason"
                        value={customLostReason}
                        onChange={(e) => setCustomLostReason(e.target.value)}
                        variant="outlined"
                        sx={{ mt: 2 }}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleLostReasonCancel} color="secondary">Cancel</Button>
                <Button 
                    onClick={handleLostReasonConfirm} 
                    color="primary" 
                    variant="contained"
                    disabled={
                        !selectedLostReason || 
                        (selectedLostReason === 'Other' && !customLostReason.trim())
                    }
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>

      </Box>
    </Container>
  );
}

export default BDDashboard;